import { useCallback, useMemo, useRef, useState, ReactElement } from "react"
import { classNames } from "../Components"
import { CloseButton } from "../Components/CloseButton"
import { Icon, Icons, IconType } from "../Components/Icon"
import { Select, OnSelectCallback, SelectOption } from "../Components/Select"

export interface MultiSelectValue {
  className?: string;
  disabled?: boolean;
  icon?: IconType;
  remove?: boolean;
  title: string | ReactElement;
  value: string;
}

interface MultiSelectProps {
  button: ReactElement | string;
  customValue?: string;
  values: (string | MultiSelectValue)[];
  name?: string;
  onSelect: OnSelectCallback;
  options: SelectOption[];
  readOnly?: boolean;
}

export const MultiSelectData = {
  normalizeValues(values: (string | MultiSelectValue)[]): string[] {
    return values.map((v) =>
      typeof v === "object" && v !== null && "value" in v ? v.value : v,
    );
  },
  addValue(current: string[], value: string): string[] {
    return [...current, value];
  },
  removeValue(current: string[], value: string): string[] {
    return current.filter((v) => v !== value);
  },
};

export function MultiSelect({ button, customValue, name, onSelect, options, readOnly, values }: MultiSelectProps) {
  const _customValueRef = useRef<HTMLInputElement | null>(null);
  const [selected, _setSelected] = useState(false);

  const selectedValues = useMemo(() => MultiSelectData.normalizeValues(values), [values]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const selectOption = useCallback((value?: any) => {
    onSelect(MultiSelectData.addValue(selectedValues, value));
  }, [selectedValues, values, onSelect]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const removeOption = useCallback((value?: any) => {
    onSelect(MultiSelectData.removeValue(selectedValues, value));
  }, [selectedValues, onSelect]);

  return (
    <div className={`MultiSelect${selected ? " selected" : ""}${readOnly ? " readOnly" : ""}`}>
      {values.map((value, index) => {
        if (typeof value === "object" && value !== null && "value" in value) {
          return (
            <span className={classNames(["MultiSelect-value", value.className])} key={`value-${index}`}>
              <span className="MultiSelect-value-text">{value.icon && <><Icon icon={value.icon} /> </>}{value.title}</span>
              {!readOnly && value.remove != false && <CloseButton icon={Icons.Trash} onClick={() => removeOption(value.value)} />}
            </span>
          );
        } else {
          return (
            <span className="MultiSelect-value" key={`value-${index}`}>
              <span className="MultiSelect-value-text">{value}</span>
              {!readOnly && <CloseButton icon={Icons.Trash} onClick={() => removeOption(value)} />}
            </span>
          );
        }
      })}
      {!readOnly && <Select button={button} customValue={customValue} name={name} onSelect={selectOption} options={options} value={undefined} />}
    </div>
  );
}
